import { GROUPED_BY_DEMAND } from "src/allocation/pages/ForecastPageDynamic/context/reducer/viewModes";
import { TEAM_TAB } from "src/consts/urlPaths";
import { teamDetailsPath } from "src/util/routing";

import type { InboxItemBase } from "../item";
import { parseInboxItemKey } from "../kinds";
import type { InboxRow } from "../row";

export type ForecastTeam = {
  id?: string | null;
  name?: string | null;
  type?: string | null;
  typeDisplay?: string | null;
  parentGroups?: Array<{
    id?: string | null;
    name?: string | null;
  } | null> | null;
};

type ForecastListTeam = {
  id?: string | null;
  name?: string | null;
  type?: string | null;
};

export type ForecastSubmissionItem = InboxItemBase & {
  kind: "FORECAST_SUBMISSION";
  teamId: string;
  allocationProjectId: string;
  cycleKey: string;
  title: string;
  subtitle: string;
  lastSubmittedFor: string | null;
  lastSubmittedAt: string | null;
  dueAt: string | null;
};

type ForecastInboxRow = InboxRow & {
  teamId?: string | null;
  allocationProjectId?: string | null;
  cycleKey?: string | null;
  lastSubmittedFor?: string | null;
  lastSubmittedAt?: string | null;
  dueAt?: string | null;
  team?: ForecastListTeam | null;
};

const text = (value: string | null | undefined): string => value?.trim() ?? "";

/** Same path the forecast mailer uses. */
export const forecastPagePath = (teamId: string): string =>
  `${teamDetailsPath(teamId, TEAM_TAB.FORECAST)}?view=${GROUPED_BY_DEMAND}`;

export const lastSubmittedLine = (
  lastSubmittedFor: string | null
): string | null =>
  lastSubmittedFor ? `Last submitted for ${lastSubmittedFor}` : null;

export const parentNameOf = (team: ForecastTeam | null): string =>
  text(team?.parentGroups?.find((parent) => text(parent?.name) !== "")?.name);

export const teamTypeOf = (team: ForecastTeam | null): string =>
  text(team?.typeDisplay) || text(team?.type);

export const parseForecastInboxRow = (
  row: InboxRow
): ForecastSubmissionItem | null => {
  if (row.kind !== "FORECAST_SUBMISSION") {
    return null;
  }
  const typed = row as ForecastInboxRow;
  const parsed = parseInboxItemKey(row.id);
  const teamId = text(typed.teamId) || text(parsed?.subjectId);
  const allocationProjectId =
    text(typed.allocationProjectId) || text(parsed?.qualifier);
  const cycleKey = text(typed.cycleKey);
  const title = text(typed.team?.name) || teamId || "Team";
  if (teamId === "" || allocationProjectId === "") {
    return null;
  }
  return {
    key: row.id,
    kind: "FORECAST_SUBMISSION",
    owedAs:
      text(typed.owedAs) || `You own ${title} · forecast not yet submitted`,
    since: row.since ?? null,
    slaHours: null,
    overdue: false,
    teamId,
    allocationProjectId,
    cycleKey,
    title,
    subtitle: cycleKey,
    lastSubmittedFor: text(typed.lastSubmittedFor) || null,
    lastSubmittedAt: typed.lastSubmittedAt ?? null,
    dueAt: typed.dueAt ?? null,
  };
};
